import { configureStore } from "@reduxjs/toolkit";
import { persistReducer, persistStore } from "redux-persist";
import createWebStorage from "redux-persist/lib/storage/createWebStorage";
import appReducer from "./reducers";

const createNoopStorage = () => ({
  getItem: () => Promise.resolve(null),
  setItem: () => Promise.resolve(),
  removeItem: () => Promise.resolve(),
});

// Use local storage on client, and a noop storage on server
const storage = typeof window !== "undefined" ? createWebStorage("local") : createNoopStorage();

const persistConfig = {
  key: "root",
  storage,
  whitelist: ["auth"], // Only persist auth-related state
};

// Root reducer with additional loading logic
const rootReducer = (state, action) => {
  const split = action.type?.split("/");
  const stateName = split?.[0];
  const typeName = split?.[split?.length - 1];
  const types = ["pending", "fulfilled", "rejected"];

  if (split?.length > 2 && types.includes(typeName) && !types.includes(stateName)) {
    const newState = { ...state };
    if (typeName === "pending") {
      newState[stateName] = {
        ...newState[stateName],
        showLoader: action.meta.arg?.showLoader === false ? false : true,
      };
    } else if (typeName === "fulfilled" || typeName === "rejected") {
      newState[stateName] = { ...newState[stateName], showLoader: false };
    }
    return appReducer(newState, action);
  }
  return appReducer(state, action);
};

// Persist reducer for client-side persistence
const persistedReducer = persistReducer(persistConfig, rootReducer);

let storeInstance; // 🔥 Store reference for API calls

// ✅ Function to create a fresh store for SSR and Client
export function createStore(preloadedState = {}) {
  const store = configureStore({
    reducer: persistedReducer,
    preloadedState, // Pass preloaded state during SSR
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
      }),
  });

  // Persistor is only needed on the client side
  const persistor = typeof window !== "undefined" ? persistStore(store) : null;
  storeInstance = store; // ✅ Save reference to the store

  return { store, persistor };
}

// ✅ Function to access store globally in API calls
export const getStore = () => storeInstance;
